import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Skeleton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { stagingApiBase } from './stagingOrg';

// Two switches per field. Rule: when ON, a staged item with that field empty is sent to
// Flagged Items (switching it changes which items are flagged, so the dashboard
// refreshes its counts through onChanged). "Can go live without it": the gap is still
// flagged and shown, but it does not block approval (description, Jason 25 Oct 2026).
// Brand (29 Oct 2026): empty = Vend sent the item with no brand; a brand mapped to "No brand"
// on Brand Mappings is blank on purpose and never counts. Since 4 Nov 2026 (Jason) its switches
// behave like every other field's; "can go live without it" is ON by default, not pinned.

type FieldKey = 'description' | 'image' | 'price' | 'tags' | 'brand';

interface MissingValueRule {
  field: FieldKey;
  enabled: boolean;
  allowLive: boolean;
}

const LABELS: Record<FieldKey, string> = {
  description: 'Description',
  image: 'Image',
  price: 'Price',
  tags: 'Tags',
  brand: 'Brand',
};

/** Fields whose "Can go live without it" is fixed ON (the backend refuses switching it off). None since 4 Nov 2026. */
const ALWAYS_ALLOW_LIVE: ReadonlySet<FieldKey> = new Set<FieldKey>([]);

const plural = (n: number) => `${n} item${n === 1 ? '' : 's'}`;

/** What a switch did to LIVE products in the app (hidden by / released from a rule). */
const liveParts = (data: any): string[] => [
  ...(data?.liveHidden > 0 ? [`${data.liveHidden} live product${data.liveHidden === 1 ? '' : 's'} hidden in the app`] : []),
  ...(data?.liveUnhidden > 0 ? [`${data.liveUnhidden} back in the app`] : []),
];

export default function MissingValueRulesTab({ onChanged }: { onChanged?: () => void }) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [rules, setRules] = useState<MissingValueRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/missing-value-rules`);
      setRules(data.rules || []);
    } catch (err: any) {
      notify(`Failed to load missing value rules: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const toggleAllowLive = async (field: FieldKey, allowLive: boolean) => {
    setBusy(field);
    setRules((prev) => prev.map((r) => (r.field === field ? { ...r, allowLive } : r)));
    try {
      const { data } = await authInstance.put(`${stagingApiBase()}/staging/missing-value-rules/${field}`, { allowLive });
      const live = liveParts(data);
      notify(`${LABELS[field]}: ${allowLive ? 'items can go live without it' : 'items must have it to go live'}${live.length ? ` (${live.join(', ')})` : ''}`, { type: 'success' });
      onChanged?.();
    } catch (err: any) {
      setRules((prev) => prev.map((r) => (r.field === field ? { ...r, allowLive: !allowLive } : r)));
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (field: FieldKey, enabled: boolean) => {
    setBusy(field);
    setRules((prev) => prev.map((r) => (r.field === field ? { ...r, enabled } : r)));
    try {
      const { data } = await authInstance.put(`${stagingApiBase()}/staging/missing-value-rules/${field}`, { enabled });
      const parts: string[] = [];
      if (data?.released > 0) parts.push(`${plural(data.released)} released to Pending Review`);
      if (data?.flagged > 0) parts.push(`${plural(data.flagged)} sent to Flagged Items`);
      parts.push(...liveParts(data));
      notify(parts.length ? parts.join(', ') : `${LABELS[field]} rule ${enabled ? 'on' : 'off'}`, { type: 'success' });
      onChanged?.();
    } catch (err: any) {
      setRules((prev) => prev.map((r) => (r.field === field ? { ...r, enabled: !enabled } : r)));
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Missing Value Rules
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          Staged items with an empty field are held in Flagged Items instead of Pending Review.
        </Typography>
      </Box>
      {loading ? (
        <Box sx={{ p: 3 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell align="right">Toggle</TableCell>
                <TableCell align="right">Can go live without it</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.field}>
                  <TableCell sx={{ color: colors.textPrimary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>
                    {LABELS[rule.field] ?? rule.field}
                  </TableCell>
                  <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                    If empty, send to Flagged Items
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Switch
                      checked={rule.enabled}
                      disabled={busy === rule.field}
                      onChange={(e) => toggle(rule.field, e.target.checked)}
                      slotProps={{ input: { role: 'switch', 'aria-label': LABELS[rule.field] ?? rule.field } }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                    {ALWAYS_ALLOW_LIVE.has(rule.field) ? (
                      <Tooltip title="A product can always go live without a brand: this rule only flags new items">
                        <span>
                          <Switch
                            color="success"
                            checked
                            disabled
                            slotProps={{ input: { role: 'switch', 'aria-label': `${LABELS[rule.field] ?? rule.field}: can go live without it` } }}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Switch
                        color="success"
                        checked={rule.allowLive}
                        disabled={busy === rule.field || !rule.enabled}
                        onChange={(e) => toggleAllowLive(rule.field, e.target.checked)}
                        slotProps={{ input: { role: 'switch', 'aria-label': `${LABELS[rule.field] ?? rule.field}: can go live without it` } }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
